import { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Loader2, AlertTriangle, KeyRound, Smartphone, CalendarRange } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from 'recharts';
import { api, type AperturasTimbres } from '@/lib/api';
import { useChartTheme, madridDay, dayLabel, Panel, Stat } from '@/components/aperturas/shared';
import type { AperturasContext } from './AperturasLayout';

/**
 * Above this many rings per identified opening, the door is mostly being opened
 * by people the system cannot name. It is a coarse screen, not proof: a guest
 * buzzed in through the entryphone also counts as a ring, so on low-traffic
 * openers the ratio says little and the rings-per-day figure says more.
 */
const ABUSE_RATIO = 3;

export default function AperturasTimbresPage() {
  const chart = useChartTheme();
  const { days } = useOutletContext<AperturasContext>();
  const [data, setData] = useState<AperturasTimbres | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    api.getAperturasTimbres(madridDay(days - 1), madridDay())
      .then((r) => { if (!cancelled) setData(r); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Error cargando datos'); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [days]);

  const esAbuso = (d: { ratioTimbre: number | null; timbre: number }) =>
    d.ratioTimbre === null ? d.timbre > 0 : d.ratioTimbre >= ABUSE_RATIO;

  const abusers = useMemo(() => (data?.byDevice ?? []).filter(esAbuso), [data]);
  const topLabels = useMemo(() => (data?.byDevice ?? []).slice(0, 5).map((d) => d.deviceLabel), [data]);

  return (
    <div className="space-y-6">
      <p className="text-sm text-slate-500 -mt-1">Uso del modo continuo de los porteros: cuántas veces se abre el portal llamando al timbre frente a las aperturas con usuario identificado.</p>

      {loading && (
        <div className="flex items-center justify-center py-20 text-slate-400">
          <Loader2 className="animate-spin mr-2" size={20} /> Cargando…
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 dark:bg-red-500/10 dark:border-red-500/30 p-4 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {data && !loading && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Stat label="Timbres" value={data.totals.timbre.toLocaleString('es-ES')} hint={`${(data.totals.timbre / data.spanDays).toFixed(1)}/día`} />
            <Stat label="Teclado" value={data.totals.keypad.toLocaleString('es-ES')} hint="huéspedes y personal" />
            <Stat label="Remotas" value={data.totals.remoto.toLocaleString('es-ES')} hint="app y Nuki Web" />
          </div>

          {abusers.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/30 p-4">
              <div className="flex items-center gap-2 font-semibold text-amber-800 dark:text-amber-300 text-sm">
                <AlertTriangle size={16} />
                {abusers.length === 1 ? '1 portero con uso desproporcionado' : `${abusers.length} porteros con uso desproporcionado`}
              </div>
              <ul className="mt-2 space-y-1 text-sm text-amber-900 dark:text-amber-200">
                {abusers.map((d) => (
                  <li key={d.smartlockId}>
                    <span className="font-medium">{d.deviceLabel}</span> — {d.timbre.toLocaleString('es-ES')} timbres
                    {' '}({d.timbresPorDia}/día){' · '}
                    {d.ratioTimbre === null
                      ? 'ninguna apertura identificada en el periodo'
                      : `${d.ratioTimbre} timbres por cada apertura identificada`}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Panel
            title="Timbres por portero"
            hint={
              <>
                En ámbar, los porteros con {ABUSE_RATIO} o más timbres por cada apertura identificada.{' '}
                <strong className="font-medium text-slate-500">Apertura identificada</strong>: la que lleva un usuario
                detrás, ya sea un PIN en el teclado o una apertura desde la app. Un timbre no lleva usuario, así que un
                huésped al que se le abre por el portero cuenta como timbre igual que un vecino. En porteros de poco
                tráfico el ratio orienta poco; ahí manda la cifra de timbres al día.
              </>
            }
          >
            <ResponsiveContainer width="100%" height={Math.max(220, data.byDevice.length * 38)}>
              <BarChart data={data.byDevice} layout="vertical" margin={{ top: 4, right: 16, left: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 12, fill: chart.axis }} />
                <YAxis type="category" dataKey="deviceLabel" width={190} tick={{ fontSize: 11, fill: chart.axis }} />
                <Tooltip cursor={{ fill: chart.cursor }} formatter={(v: any) => [`${Number(v).toLocaleString('es-ES')} timbres`, '']} />
                <Bar dataKey="timbre" radius={[0, 4, 4, 0]}>
                  {data.byDevice.map((d, i) => (
                    <Cell key={d.smartlockId} fill={esAbuso(d) ? '#D97706' : chart.palette[i % chart.palette.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Panel>

          <Panel title="Evolución diaria" hint="Los 5 porteros con más timbres del periodo.">
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={data.series} margin={{ top: 4, right: 16, left: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                <XAxis dataKey="day" tickFormatter={dayLabel} tick={{ fontSize: 11, fill: chart.axis }} minTickGap={24} />
                <YAxis tick={{ fontSize: 12, fill: chart.axis }} allowDecimals={false} />
                <Tooltip cursor={{ stroke: chart.grid }} />
                {topLabels.map((label, i) => (
                  <Line key={label} type="monotone" dataKey={label} stroke={chart.palette[i % chart.palette.length]} strokeWidth={2} dot={false} connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Panel>

          <Panel title="Franja horaria" hint="Hora de Madrid. Las llamadas de madrugada son la señal más clara de mal uso.">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.hours} margin={{ top: 4, right: 16, left: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                <XAxis dataKey="hour" tickFormatter={(h) => `${h}h`} tick={{ fontSize: 11, fill: chart.axis }} />
                <YAxis tick={{ fontSize: 12, fill: chart.axis }} allowDecimals={false} />
                <Tooltip cursor={{ fill: chart.cursor }} labelFormatter={(h) => `${h}:00 – ${h}:59`} formatter={(v: any) => [`${v} timbres`, '']} />
                <Bar dataKey="timbres" fill={chart.bar} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Panel>

          <div className="rounded-xl border border-slate-200 dark:border-white/10 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-white/5 text-slate-500">
                <tr>
                  <th className="text-left font-medium px-4 py-2.5">Portero</th>
                  <th className="text-right font-medium px-4 py-2.5">Timbres</th>
                  <th className="text-right font-medium px-4 py-2.5">Por día</th>
                  <th className="text-right font-medium px-4 py-2.5"><KeyRound size={13} className="inline" /> Teclado</th>
                  <th className="text-right font-medium px-4 py-2.5"><Smartphone size={13} className="inline" /> Remotas</th>
                  <th className="text-right font-medium px-4 py-2.5">Timbres / identificada</th>
                </tr>
              </thead>
              <tbody>
                {data.byDevice.map((d) => (
                  <tr key={d.smartlockId} className="border-t border-slate-100 dark:border-white/5">
                    <td className="px-4 py-2.5 text-sereno-dark">{d.deviceLabel}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{d.timbre.toLocaleString('es-ES')}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{d.timbresPorDia}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{d.keypad}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{d.remoto}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-medium">
                      {d.ratioTimbre === null ? '—' : `${d.ratioTimbre}×`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-xs text-slate-400 flex items-center gap-1.5">
            <CalendarRange size={13} /> Datos de sereno_shared/nuki_events, el mismo origen que el resto de la sección.
          </p>
        </>
      )}
    </div>
  );
}
